import { PlusCircle, Trash2 } from 'lucide-react';
import { subprofileDetailPath } from '../../../../lib/peopleRoutes';
import { beziehungsText } from '../../../../lib/subprofileRelationships';
import type { Kontaktweg } from '../../../../lib/api/stammdaten';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { useLocation, useNavigate } from 'react-router-dom';
import PersonCollapsibleSection from '../PersonCollapsibleSection';

type AssociatedPerson = {
  id: string;
  index: number;
  name: string;
  birthDate?: string;
  relationshipType?: string;
  relationshipLabel?: string;
  parentName?: string;
  communications?: Kontaktweg[];
  communicationChannels?: Kontaktweg[];
};

/** Die eigenen Kontaktwege eines Schülerprofils in einer Zeile (T-0289). */
const kontaktZeile = (sp: AssociatedPerson): string => {
  const wege = Array.isArray(sp.communications)
    ? sp.communications
    : Array.isArray(sp.communicationChannels)
      ? sp.communicationChannels
      : [];
  const werte = wege.map((weg) => String(weg.value || '').trim()).filter(Boolean);
  return werte.length > 0 ? werte.join(' · ') : 'Kontakt über das Kundenkonto';
};

type Props = {
  visible: boolean;
  personId: string;
  subprofiles: AssociatedPerson[];
  onAdd: () => void;
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
};

export default function PersonAssociatedPersonsTab({
  visible,
  personId,
  subprofiles,
  onAdd,
  onEdit,
  onDelete,
}: Readonly<Props>) {
  const location = useLocation();
  const navigate = useNavigate();
  if (!visible) return null;

  return (
    <PersonCollapsibleSection
      title="Zugeordnete Profile"
      description="Schülerprofile, die diesem Kunden zugeordnet sind"
      defaultOpen
      actions={(
        <Button onClick={onAdd}>
          <PlusCircle className="mr-2 h-4 w-4" />
          Zugeordnete Person hinzufügen
        </Button>
      )}
    >
      {subprofiles.length === 0 ? (
        <div className="rounded-lg border p-8 text-center text-sm text-muted-foreground">
          Keine zugeordneten Personen vorhanden
        </div>
      ) : (
        <div className="space-y-2">
          {subprofiles.map((sp, idx) => (
            <div key={sp.id} className="flex flex-wrap items-center gap-3 rounded-lg border p-3">
              <Badge variant="secondary">Schüler</Badge>
              <div className="min-w-0 flex-1">
                <div className="truncate">
                  <span className="font-semibold">{sp.name}</span>
                  <span className="text-muted-foreground"> – {beziehungsText(sp)}</span>
                </div>
                {sp.index !== 1 ? (
                  <div className="truncate text-xs text-muted-foreground">{kontaktZeile(sp)}</div>
                ) : null}
              </div>
              <div className="flex items-center gap-2">
                {sp.index !== 1 ? (
                  <Button variant="secondary" size="sm" onClick={() => onEdit(idx)}>
                    Profil bearbeiten
                  </Button>
                ) : null}
                <Button size="sm" onClick={() => navigate(subprofileDetailPath(personId, sp.index, location.pathname))}>
                  zum Profil
                </Button>
                {sp.index !== 1 ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive"
                    onClick={() => onDelete(idx)}
                    title="Zugeordnete Person löschen"
                    aria-label={`${sp.name} löschen`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </PersonCollapsibleSection>
  );
}
